(() => {
  if (window.hcProductShowcasesInitialized) return;
  window.hcProductShowcasesInitialized = true;
  const instances = new WeakMap();
  const query = `query ShopifyShowcases($sku: String!, $skip: Int!, $take: Int!) {
    showcases(sku: $sku, skip: $skip, take: $take) {
      items { id imageUrl }
      totalCount
    }
  }`;

  class HcProductShowcases {
    constructor(root) {
      this.root = root;
      this.section = root.closest('[data-hc-showcases-section]');
      this.section.hidden = false;
      this.controller = new AbortController();
      this.status = root.querySelector('[role="status"]');
      this.skeleton = root.querySelector('.hc-showcases__skeleton');
      this.retry = root.querySelector('.hc-showcases__retry');
      this.slider = root.querySelector('.hc-showcases__slider');
      this.controls = root.querySelector('[data-slider-controls]');
      const options = { signal: this.controller.signal };
      this.retry.addEventListener('click', () => this.load(), options);
      root.querySelector('[data-prev]').addEventListener('click', () => this.swiper?.slidePrev(), options);
      root.querySelector('[data-next]').addEventListener('click', () => this.swiper?.slideNext(), options);
      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        if (this.visible && !this.loaded && !this.loading && !this.failed) this.load();
        this.syncAutoplay();
      });
      this.observer.observe(root);
    }

    destroy() {
      this.controller.abort();
      this.observer.disconnect();
      this.swiper?.destroy(true, true);
      this.swiper = null;
    }

    syncAutoplay() {
      if (!this.swiper?.autoplay) return;
      if (this.visible) this.swiper.autoplay.resume();
      else this.swiper.autoplay.pause();
    }

    async load() {
      if (this.loading) return;
      this.loading = true;
      this.failed = false;
      this.retry.hidden = true;
      this.status.hidden = true;
      this.skeleton.hidden = false;
      this.slider.hidden = true;
      this.controls.hidden = true;
      this.root.setAttribute('aria-busy', 'true');
      const signal = this.controller.signal;
      try {
        const sku = this.root.dataset.sku.trim();
        if (!sku) throw new Error('Set a HEY CLAY SKU in the section settings.');
        const items = new Map();
        let skip = 0;
        let totalCount;
        const response = await fetch(this.root.dataset.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'omit',
          body: JSON.stringify({ query, variables: { sku, skip: 0, take: 8 } }),
          signal: AbortSignal.any([signal, AbortSignal.timeout(20000)]),
        });
        if (!response.ok) throw new Error(`Showcases HTTP ${response.status}`);
        const result = await response.json();
        if (result.errors?.length) throw new Error(result.errors.map(error => error.message).join('; '));
        const page = result.data?.showcases;
        if (!Array.isArray(page?.items) || !Number.isInteger(page.totalCount) || page.totalCount < 0) {
          throw new Error('Invalid showcases response');
        }
        totalCount = page.totalCount;
        if (!page.items.length && skip < totalCount) throw new Error('Incomplete showcases response');
        for (const item of page.items) {
          if (!item?.id || !item.imageUrl) continue;
          const url = new URL(item.imageUrl);
          if (url.protocol === 'https:') items.set(item.id, url.href);
        }

        if (signal.aborted) return;
        if (!items.size) {
          this.section.hidden = true;
          this.loaded = true;
          return;
        }
        // The theme owns the Swiper CDN script; allow deferred/async loading.
        const deadline = Date.now() + 10000;
        while (!window.Swiper) {
          signal.throwIfAborted();
          if (Date.now() > deadline) throw new Error('The theme must load the Swiper bundle.');
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        signal.throwIfAborted();
        const wrapper = this.root.querySelector('.swiper-wrapper');
        wrapper.replaceChildren();
        let index = 0;
        for (const url of items.values()) {
          const slide = document.createElement('div');
          slide.className = 'swiper-slide';
          const image = document.createElement('img');
          image.src = url;
          image.alt = `${this.root.querySelector('h2').textContent.trim()} ${++index}`;
          image.loading = 'lazy';
          image.decoding = 'async';
          slide.append(image);
          wrapper.append(slide);
        }
        this.skeleton.hidden = true;
        this.slider.hidden = false;
        this.controls.hidden = items.size < 2;
        this.swiper = new window.Swiper(this.slider, {
          slidesPerView: 2.2,
          spaceBetween: 8,
          grabCursor: true,
          watchOverflow: true,
          breakpoints: {
            640: { slidesPerView: 3.2, spaceBetween: 8 },
            1024: { slidesPerView: 4.5, spaceBetween: 12 },
          },
          autoplay: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? false : {
            delay: 4000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          },
          scrollbar: { el: this.root.querySelector('.hc-showcases__scrollbar'), draggable: true },
          on: {
            activeIndexChange(swiper) {
              if (!swiper.params.autoplay) return;
              if (swiper.isEnd) swiper.params.autoplay.reverseDirection = true;
              else if (swiper.isBeginning) swiper.params.autoplay.reverseDirection = false;
            },
          },
        });
        this.syncAutoplay();
        this.loaded = true;
      } catch (error) {
        if (signal.aborted) return;
        this.failed = true;
        this.skeleton.hidden = true;
        this.slider.hidden = true;
        this.controls.hidden = true;
        this.status.hidden = false;
        this.status.textContent = this.root.querySelector('[data-error]').content.textContent;
        this.retry.hidden = false;
        console.warn('[HC showcases]', error);
      } finally {
        this.loading = false;
        this.root.setAttribute('aria-busy', 'false');
      }
    }
  }

  function initialize(scope) {
    scope.querySelectorAll('[data-hc-showcases]').forEach(root => {
      if (!instances.has(root)) instances.set(root, new HcProductShowcases(root));
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  } else {
    initialize(document);
  }
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-hc-showcases]').forEach(root => {
      instances.get(root)?.destroy();
      instances.delete(root);
    });
  });
})();
